// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skein — the bead-drag input (principle #19: the control fits the game). Untangling IS a
// direct spatial manipulation, so the scheme is: press the nearest bead (hit radius ≥44px,
// independent of the drawn size), drag it — the bead keeps its grab offset so it does not
// jump — and drop to commit one move. Desktop keeps a full keyboard a11y path: [ / ] (or
// Tab) cycle the selected bead, Space/Enter grabs & drops it, and the arrows nudge a grabbed
// bead. Pointer Events only, per-pointer, clientX/Y − getBoundingClientRect (never offset).

export interface DragDeps {
  /** Nearest grabbable bead to a CSS point in the host, or null. */
  nodeAt: (cssX: number, cssY: number) => number | null;
  /** CSS point in the host → normalized [0,1] board coords. */
  toNorm: (cssX: number, cssY: number) => { x: number; y: number };
  /** A bead's current centre in CSS px within the host. */
  nodeCenterCss: (i: number) => { x: number; y: number } | null;
  /** A bead's committed normalized position. */
  nodeNorm: (i: number) => { x: number; y: number } | null;
  /** All bead centres in CSS px (for keyboard directional selection). */
  centersCss: () => Array<{ x: number; y: number }>;
  onGrab: (i: number) => void;
  onDrag: (i: number, nx: number, ny: number) => void;
  onRelease: (i: number, nx: number, ny: number) => void;
  onSelect: (i: number | null) => void;
}

export interface DragInput {
  dispose(): void;
}

const KB_STEP = 0.028;

export function createDragInput(host: HTMLElement, deps: DragDeps): DragInput {
  let drag: { id: number; node: number; offX: number; offY: number; nx: number; ny: number } | null = null;
  let selected: number | null = null;
  let kbGrab: { node: number; x: number; y: number } | null = null;

  const local = (e: PointerEvent): { x: number; y: number } => {
    const r = host.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onDown = (e: PointerEvent): void => {
    if (drag) return;
    const p = local(e);
    const node = deps.nodeAt(p.x, p.y);
    if (node === null) return;
    e.preventDefault();
    const c = deps.nodeCenterCss(node);
    const offX = c ? c.x - p.x : 0;
    const offY = c ? c.y - p.y : 0;
    const n = deps.toNorm(p.x + offX, p.y + offY);
    drag = { id: e.pointerId, node, offX, offY, nx: n.x, ny: n.y };
    selected = node;
    try {
      host.setPointerCapture(e.pointerId);
    } catch {
      /* capture is best-effort */
    }
    deps.onGrab(node);
    deps.onSelect(node);
  };

  const onMove = (e: PointerEvent): void => {
    if (!drag || e.pointerId !== drag.id) return;
    const p = local(e);
    const n = deps.toNorm(p.x + drag.offX, p.y + drag.offY);
    drag.nx = n.x;
    drag.ny = n.y;
    deps.onDrag(drag.node, n.x, n.y);
  };

  const finishDrag = (): void => {
    if (!drag) return;
    const d = drag;
    drag = null;
    deps.onRelease(d.node, d.nx, d.ny);
  };

  const onUp = (e: PointerEvent): void => {
    if (!drag || e.pointerId !== drag.id) return;
    finishDrag();
  };

  const onCancel = (e: PointerEvent): void => {
    if (!drag || e.pointerId !== drag.id) return;
    finishDrag();
  };

  // ── keyboard ─────────────────────────────────────────────────────────────
  const cycle = (dir: number): void => {
    const n = deps.centersCss().length;
    if (n === 0) return;
    selected = selected === null ? 0 : (selected + dir + n) % n;
    deps.onSelect(selected);
  };

  const nearestInDir = (dx: number, dy: number): void => {
    const centers = deps.centersCss();
    if (selected === null) {
      cycle(1);
      return;
    }
    const from = centers[selected];
    let best = -1;
    let bestScore = Infinity;
    for (let i = 0; i < centers.length; i++) {
      if (i === selected) continue;
      const vx = centers[i].x - from.x;
      const vy = centers[i].y - from.y;
      const along = vx * dx + vy * dy;
      if (along <= 0) continue;
      const perp = Math.abs(vx * dy - vy * dx);
      const score = perp * 2 + Math.hypot(vx, vy);
      if (score < bestScore) {
        bestScore = score;
        best = i;
      }
    }
    if (best >= 0) {
      selected = best;
      deps.onSelect(best);
    }
  };

  const onKey = (e: KeyboardEvent): void => {
    const key = e.key;
    if (key === ']' || (key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      cycle(1);
      return;
    }
    if (key === '[' || (key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      cycle(-1);
      return;
    }
    if (key === ' ' || key === 'Enter') {
      e.preventDefault();
      if (kbGrab) {
        deps.onRelease(kbGrab.node, kbGrab.x, kbGrab.y);
        kbGrab = null;
      } else if (selected !== null) {
        const n = deps.nodeNorm(selected);
        if (n) {
          kbGrab = { node: selected, x: n.x, y: n.y };
          deps.onGrab(selected);
        }
      }
      return;
    }
    let dx = 0;
    let dy = 0;
    if (key === 'ArrowLeft') dx = -1;
    else if (key === 'ArrowRight') dx = 1;
    else if (key === 'ArrowUp') dy = -1;
    else if (key === 'ArrowDown') dy = 1;
    else return;
    e.preventDefault();
    if (kbGrab) {
      kbGrab.x = Math.max(0, Math.min(1, kbGrab.x + dx * KB_STEP));
      kbGrab.y = Math.max(0, Math.min(1, kbGrab.y + dy * KB_STEP));
      deps.onDrag(kbGrab.node, kbGrab.x, kbGrab.y);
    } else {
      nearestInDir(dx, dy);
    }
  };

  host.addEventListener('pointerdown', onDown);
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onCancel);
  window.addEventListener('keydown', onKey);

  return {
    dispose() {
      host.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey);
      drag = null;
      kbGrab = null;
    },
  };
}
